import React, { useEffect, useState, useCallback } from "react";
import { DashboardSelect } from "../../../components/Input/Input";
import Input from "../../../components/Input/Input";
import { Button, Card, CardContent, Typography, FormControl, Select, MenuItem, Alert, IconButton, Switch, FormControlLabel, Tooltip } from "@mui/material";
import { DragIndicator, Add as AddIcon, Delete as DeleteIcon, Tune as FormatIcon, Info as InfoIcon } from "@mui/icons-material";

import "./TemplateReports.css";

import { openPopup } from "../../../stateManagement/actions/popupActions";
import { TemplateReportService, formatDisplayName, isDateColumn, isNumberColumn } from "../../../services/templateReportService";

import { DATE_FORMAT_OPTIONS, NUMBER_FORMAT_OPTIONS, ERROR_MESSAGES, UI_CONSTANTS, PLACEHOLDERS, FORMAT_PATTERNS, FORMAT_EXAMPLES, DEFAULT_FORMATS } from "./constants";

// TypeScript interfaces for type safety
export interface ColumnMetadata {
  label: string;
  type: string;
  default?: boolean;
}

export interface TemplateOption {
  id: string;
  name: string;
  type: string;
  role: string;
  filter: any;
  globalFilters: any[] | null;
  query?: string;
  default_columns?: string;
  all_columns?: Record<string, ColumnMetadata>;
}

export interface SelectedColumn {
  id: string;
  originalColumn: string;
  displayName: string;
  columnType: string;
  customHeader: string;
  dateFormat?: string;
  numberFormat?: string;
  hasError?: boolean;
}

// Add type definitions at the top of the file
type FormatState = Record<string, string>;
type FormatErrorState = Record<string, string>;

const TemplateReports: React.FC = () => {
  // State management
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateOption | null>(null); // Currently selected template
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]); // List of available templates
  const [availableColumns, setAvailableColumns] = useState<Record<string, ColumnMetadata>>({}); // Available columns for the selected template
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>([]); // Columns selected for the report
  const [reportName, setReportName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showCustomDateFormat, setShowCustomDateFormat] = useState<Record<string, boolean>>({});
  const [showCustomNumberFormat, setShowCustomNumberFormat] = useState<Record<string, boolean>>({});
  const [customDateFormat, setCustomDateFormat] = useState<FormatState>({});
  const [customNumberFormat, setCustomNumberFormat] = useState<FormatState>({});
  const [enableEmail, setEnableEmail] = useState<boolean>(false);
  const [editingColumnNumber, setEditingColumnNumber] = useState<number | null>(null);
  const [newColumnPosition, setNewColumnPosition] = useState<string>("");
  const [expandedFormat, setExpandedFormat] = useState<string | null>(null);
  const [formatErrors, setFormatErrors] = useState<FormatErrorState>({});

  // Effects
  useEffect(() => {
    fetchReportTemplates();
  }, []);

  // API calls
  const fetchReportTemplates = async () => {
    setLoading(true);
    try {
      const templates = await TemplateReportService.fetchTemplates();
      
      // Ensure we have an array of templates
      if (Array.isArray(templates)) {
        setTemplateOptions(templates);
      } else if (templates && typeof templates === 'object') {
        // Handle case where API returns an object with data property
        const templateObj = templates as { data?: TemplateOption[]; templates?: TemplateOption[] };
        if ('data' in templateObj && Array.isArray(templateObj.data)) {
          setTemplateOptions(templateObj.data);
        } else if ('templates' in templateObj && Array.isArray(templateObj.templates)) {
          setTemplateOptions(templateObj.templates);
        } else {
          setTemplateOptions([]);
        }
      } else {
        setTemplateOptions([]);
      }
    } catch (error) {
      openPopup("Error", "Failed to load report templates");
      setTemplateOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleTemplateChange = async (templateName: string) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const template = Array.isArray(templateOptions) ? templateOptions.find((t) => t.name === templateName) : null;
      if (!template) return;

      setSelectedTemplate(template);
      setReportName(formatDisplayName(template.name));
      
      // Handle the new API structure with allColumns
      const availableCols = template.all_columns || {};
      setAvailableColumns(availableCols);

      if (template.default_columns) {
        const defaultCols = template.default_columns.split(",").map((col) => col.trim());
        const defaultSelectedColumns: SelectedColumn[] = defaultCols
          .map((col, index) => {
            // Find column by key (original column name) instead of label
            const columnMeta = availableCols[col];
            
            if (!columnMeta) return null;

            return {
              id: `${col}_${Date.now()}_${index}`,
              originalColumn: col,
              displayName: columnMeta.label,
              columnType: columnMeta.type,
              customHeader: formatDisplayName(columnMeta.label),
              dateFormat: isDateColumn(columnMeta) ? DEFAULT_FORMATS.date : undefined,
              numberFormat: isNumberColumn(columnMeta) ? DEFAULT_FORMATS[columnMeta.type as keyof typeof DEFAULT_FORMATS] : undefined,
            };
          })
          .filter(Boolean) as SelectedColumn[];

        setSelectedColumns(defaultSelectedColumns);
      } else {
        setSelectedColumns([]);
      }
    } catch (error) {
      openPopup("Error", "Failed to load template configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    if (!selectedTemplate || !Array.isArray(selectedColumns) || selectedColumns.length === 0) {
      openPopup("Error", ERROR_MESSAGES.NO_TEMPLATE_SELECTED);
      return;
    }

    const hasDuplicates = Array.isArray(selectedColumns) && selectedColumns.some((col) => col.hasError);
    if (hasDuplicates) {
      setErrorMessage(ERROR_MESSAGES.DUPLICATE_HEADERS);
      return;
    }

    setLoading(true);
    try {
      const authContext = JSON.parse(localStorage.getItem('authContext') || '{}');
      const userEmail = authContext.user?.email;

      if (enableEmail && !userEmail) {
        openPopup("Error", "User email not found. Please login again.");
        setLoading(false);
        return;
      }

      // Build legacy config
      const legacyConfig = {
        name: reportName,
        template: selectedTemplate.name,
        columns: selectedColumns.map((col) => ({
          originalColumn: col.originalColumn,
          displayName: col.displayName,
          customHeader: col.customHeader,
          columnType: col.columnType,
          dateFormat: col.dateFormat,
          numberFormat: col.numberFormat,
          textFormat: undefined,
        })),
        query: selectedTemplate.query || '',
        isEmail: enableEmail,
      };

      // Create report using the service with the new flat payload
      await TemplateReportService.createReport(legacyConfig, selectedTemplate, enableEmail);
      openPopup("Success", enableEmail ? "Report has been sent to your email!" : "Report configuration saved successfully!");
      handleReset();
    } catch (error) {
      openPopup("Error", "Failed to save report configuration");
    } finally {
      setLoading(false);
    }
  };

  // Column management
  const addColumn = useCallback((columnKey: string, columnMeta: ColumnMetadata) => {
    const newColumn: SelectedColumn = {
      id: `${columnKey}_${Date.now()}`,
      originalColumn: columnKey,
      displayName: columnMeta.label,
      columnType: columnMeta.type,
      customHeader: formatDisplayName(columnMeta.label),
      dateFormat: isDateColumn(columnMeta) ? DEFAULT_FORMATS.date : undefined,
      numberFormat: isNumberColumn(columnMeta) ? DEFAULT_FORMATS.number : undefined,
    };

    setSelectedColumns((prev) => [...prev, newColumn]);
    setErrorMessage("");
  }, []);

  const removeColumn = useCallback((columnId: string) => {
    setSelectedColumns((prev) => prev.filter((col) => col.id !== columnId));
    // Clean up custom format states
    setShowCustomDateFormat(prev => {
      const { [columnId]: _, ...rest } = prev;
      return rest;
    });
    setShowCustomNumberFormat(prev => {
      const { [columnId]: _, ...rest } = prev;
      return rest;
    });
    setCustomDateFormat(prev => {
      const { [columnId]: _, ...rest } = prev;
      return rest as FormatState;
    });
    setCustomNumberFormat(prev => {
      const { [columnId]: _, ...rest } = prev;
      return rest as FormatState;
    });
    setErrorMessage("");
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();

    if (!draggedColumn) return;

    const sourceIndex = Array.isArray(selectedColumns) ? selectedColumns.findIndex((col) => col.id === draggedColumn) : -1;
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;

    const newColumns = [...selectedColumns];
    const [movedColumn] = newColumns.splice(sourceIndex, 1);
    newColumns.splice(targetIndex, 0, movedColumn);

    setSelectedColumns(newColumns);
    setDraggedColumn(null);
    setDragOverIndex(null);
  }, [draggedColumn, selectedColumns]);

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDragOverIndex(null);
  }, []);

  const handleHeaderChange = useCallback((columnId: string, newHeader: string) => {
    // Check for duplicate headers
    const isDuplicate = Array.isArray(selectedColumns) && selectedColumns.some(
      (col) => col.id !== columnId && col.customHeader.toLowerCase() === newHeader.toLowerCase()
    );

    setSelectedColumns((prev) =>
      prev.map((col) =>
        col.id === columnId 
          ? { 
              ...col, 
              customHeader: newHeader,
              hasError: isDuplicate 
            } 
          : col
      )
    );

    if (isDuplicate) {
      setErrorMessage("Duplicate header names are not allowed. Please use unique names for each column.");
    } else {
      setErrorMessage("");
    }
  }, [selectedColumns]);

  const handleColumnDateFormatChange = useCallback((columnId: string, format: string) => {
    if (format === 'custom') {
      setShowCustomDateFormat(prev => ({ ...prev, [columnId]: true }));
      // Keep the format as 'custom' in the select bar
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, dateFormat: 'custom' } : col
        )
      );
      return;
    }
    setShowCustomDateFormat(prev => ({ ...prev, [columnId]: false }));
    setSelectedColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, dateFormat: format } : col
      )
    );
  }, []);

  const validateDateFormat = useCallback((format: string): boolean => {
    // Allow any format that contains valid date format characters
    return FORMAT_PATTERNS.date.pattern.test(format);
  }, []);
  

  const handleCustomDateFormatChange = useCallback((columnId: string, format: string) => {
    // Allow empty string or space
    if (!format.trim()) {
      setCustomDateFormat(prev => ({ ...prev, [columnId]: ' ' }));
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, dateFormat: ' ' } : col
        )
      );
      setFormatErrors(prev => ({ ...prev, [columnId]: '' }));
      return;
    }

    const isValid = validateDateFormat(format);
    setFormatErrors(prev => ({
      ...prev,
      [columnId]: isValid ? '' : FORMAT_PATTERNS.date.message
    }));

    setCustomDateFormat(prev => ({ ...prev, [columnId]: format }));
    if (isValid) {
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, dateFormat: format } : col
        )
      );
    }
  }, [validateDateFormat]);

  const handleColumnNumberFormatChange = useCallback((columnId: string, format: string) => {
    if (format === 'custom') {
      setShowCustomNumberFormat(prev => ({ ...prev, [columnId]: true }));
      // Keep the format as 'custom' in the select bar
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, numberFormat: 'custom' } : col
        )
      );
      return;
    }
    setShowCustomNumberFormat(prev => ({ ...prev, [columnId]: false }));
    setSelectedColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, numberFormat: format } : col
      )
    );
  }, []);

  const validateNumberFormat = useCallback((format: string): boolean => {
    // Allow any format that contains valid number format characters
    return FORMAT_PATTERNS.number.pattern.test(format);
  }, []);

  const handleCustomNumberFormatChange = useCallback((columnId: string, format: string) => {
    // Allow empty string or space
    if (!format.trim()) {
      setCustomNumberFormat(prev => ({ ...prev, [columnId]: ' ' }));
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, numberFormat: ' ' } : col
        )
      );
      setFormatErrors(prev => ({ ...prev, [columnId]: '' }));
      return;
    }

    const isValid = validateNumberFormat(format);
    setFormatErrors(prev => ({
      ...prev,
      [columnId]: isValid ? '' : FORMAT_PATTERNS.number.message
    }));

    setCustomNumberFormat(prev => ({ ...prev, [columnId]: format }));
    if (isValid) {
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, numberFormat: format } : col
        )
      );
    }
  }, [validateNumberFormat]);

  const handleReset = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedColumns([]);
    setAvailableColumns({});
    setReportName("");
    setErrorMessage("");
    // Reset all custom format states
    setShowCustomDateFormat({});
    setShowCustomNumberFormat({});
    setCustomDateFormat({});
    setCustomNumberFormat({});
  }, []);

  // Computed values
  const getAvailableColumnsForSelection = useCallback(() => {
    if (!Array.isArray(selectedColumns)) return [];
    
    const selectedColumnKeys = new Set(selectedColumns.map((col) => col.originalColumn));
    return Object.entries(availableColumns)
      .filter(([columnKey]) => !selectedColumnKeys.has(columnKey))
      .map(([columnKey, columnMeta]) => ({
        value: columnKey,
        label: formatDisplayName(columnMeta.label)
      }));
  }, [availableColumns, selectedColumns]);

  const availableColumnOptions = getAvailableColumnsForSelection();

  // Add this new function to handle position change
  const handlePositionChange = useCallback((currentIndex: number, newPosition: string) => {
    if (!newPosition.trim()) {
      setEditingColumnNumber(null);
      return;
    }

    const newPos = parseInt(newPosition);
    if (isNaN(newPos) || newPos < 1 || newPos > (Array.isArray(selectedColumns) ? selectedColumns.length : 0)) {
      setEditingColumnNumber(null);
      return;
    }

    const newIndex = newPos - 1;
    if (newIndex === currentIndex) {
      setEditingColumnNumber(null);
      return;
    }

    setSelectedColumns((prev) => {
      const newColumns = [...prev];
      const [movedColumn] = newColumns.splice(currentIndex, 1);
      newColumns.splice(newIndex, 0, movedColumn);
      return newColumns;
    });
    setEditingColumnNumber(null);
  }, [selectedColumns]);

  // Add this function to handle number click
  const handleNumberClick = useCallback((index: number) => {
    setEditingColumnNumber(index);
    setNewColumnPosition((index + 1).toString());
  }, []);

  // Add this function to handle input blur
  const handlePositionInputBlur = useCallback(() => {
    if (editingColumnNumber !== null) {
      handlePositionChange(editingColumnNumber, newColumnPosition);
    }
  }, [editingColumnNumber, newColumnPosition, handlePositionChange]);

  // Add this function to handle input key press
  const handlePositionInputKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingColumnNumber !== null) {
        handlePositionChange(editingColumnNumber, newColumnPosition);
      }
    } else if (e.key === 'Escape') {
      setEditingColumnNumber(null);
    }
  }, [editingColumnNumber, newColumnPosition, handlePositionChange]);

  // Add this function to handle format expansion
  const handleFormatExpand = useCallback((columnId: string) => {
    setExpandedFormat((prev) => (prev === columnId ? null : columnId));
  }, []);

  const getFormatExample = useCallback((format: string, type: 'date' | 'number'): string => {
    const examples = FORMAT_EXAMPLES[type];
    return (examples as Record<string, string>)[format] || '';
  }, []);

  return (
    <div className="template-reports-container">
      <div className="template-reports-header">
        <Typography variant="h4" component="h1" className="page-title">
          Template Report Generator
        </Typography>
        <Typography variant="body1" className="page-description">
          Create custom reports based on templates
        </Typography>
      </div>

      <Card className="template-selection-card">
        <CardContent>
          <div className="step-header">
            <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
              Step 1: Select Report Template
            </Typography>
          </div>
          <DashboardSelect
            className="template-select"
            label="Choose Template"
            selectLableId="reportTemplate"
            options={Array.isArray(templateOptions) ? templateOptions.map((t) => t.name) : []}
            value={selectedTemplate?.name || ""}
            name="template"
            onChange={(e) => handleTemplateChange(e.target.value as string)}
            disabled={loading}
          />
        </CardContent>
      </Card>

      {selectedTemplate && (
        <>
          <Card className="report-config-card">
            <CardContent>
              <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
                Step 2: Configure Report
              </Typography>

              <div className="report-name-section">
                <Input
                  type="text"
                  label="Report Name"
                  value={reportName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setReportName(e.target.value)
                  }
                  className="report-name-input"
                  placeholder={PLACEHOLDERS.REPORT_NAME}
                />
              </div>
            </CardContent>
          </Card>

          {errorMessage && (
            <Alert severity="error" className="error-alert">
              {errorMessage}
            </Alert>
          )}

          <Card className="column-headers-card">
            <CardContent>
              <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
                Step 3: Customize Selected Columns {Array.isArray(selectedColumns) && selectedColumns.length > 0 && `(${selectedColumns.length})`}
              </Typography>
                              {Array.isArray(selectedColumns) && selectedColumns.length > 0 && (
                <div className="selected-columns-grid">
                  {Array.isArray(selectedColumns) && selectedColumns.map((column, index) => (
                    <div
                      key={column.id}
                      className={`column-header-item ${
                        dragOverIndex === index ? "drag-over" : ""
                      } ${column.hasError ? "has-error" : ""} ${
                        expandedFormat === column.id ? "expanded" : ""
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, column.id)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {editingColumnNumber === index ? (
                        <div className="column-number-editing">
                          <input
                            type="number"
                            value={newColumnPosition}
                            onChange={(e) => setNewColumnPosition(e.target.value)}
                            onBlur={handlePositionInputBlur}
                            onKeyDown={handlePositionInputKeyPress}
                            min={1}
                            max={selectedColumns.length}
                            className="column-number-input"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div 
                          className="column-number" 
                          onClick={() => handleNumberClick(index)}
                          title="Click to change position"
                        >
                          {index + 1}
                        </div>
                      )}
                      
                      <div className="column-header-top">
                        <div className="column-left">
                          <div className="column-drag-handle" title="Drag to reorder">
                            <DragIndicator fontSize="small" />
                          </div>

                          <div className="column-info">
                            <Typography variant="body2" className="column-display-name">
                              {formatDisplayName(column.displayName)}
                            </Typography>
                          </div>
                        </div>

                        <div className="column-right">
                          <Input
                            type="text"
                            placeholder="Custom Header"
                            value={column.customHeader}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleHeaderChange(column.id, e.target.value)
                            }
                            className="header-input"
                            fullWidth
                            style={{ minWidth: 180, maxWidth: 320 }}
                          />

                          {(column.columnType === 'date' || column.columnType === 'datetime' || column.columnType === 'number') && (
                            <div className="format-button" onClick={() => handleFormatExpand(column.id)}>
                              <IconButton
                                size="small"
                                className="format-icon"
                                title="Format Settings"
                              >
                                <FormatIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}

                          <IconButton
                            size="small"
                            onClick={() => removeColumn(column.id)}
                            className="remove-column-icon"
                            title="Remove Column"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>

                      {expandedFormat === column.id && (
                        <div className="column-format-controls">
                          {column.dateFormat && (
                            <div className="format-control">
                              <div className="format-content">
                                <FormControl size="small" className="format-select">
                                  <Select
                                    value={column.dateFormat}
                                    onChange={(e) =>
                                      handleColumnDateFormatChange(
                                        column.id,
                                        e.target.value as string
                                      )
                                    }
                                    label="Date Format"
                                  >
                                    {DATE_FORMAT_OPTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        <div className="format-option">
                                          <span>{option.label}</span>
                                          {option.value !== 'custom' && (
                                            <span className="format-example">
                                              {getFormatExample(option.value, 'date')}
                                            </span>
                                          )}
                                        </div>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {showCustomDateFormat[column.id] && (
                                  <div className="custom-format-container">
                                    <Input
                                      type="text"
                                      placeholder={PLACEHOLDERS.CUSTOM_DATE_FORMAT}
                                      value={customDateFormat[column.id] || column.dateFormat || ''}
                                      onChange={(e) => handleCustomDateFormatChange(column.id, e.target.value)}
                                      className="custom-format-input"
                                      error={!!formatErrors[column.id]}
                                      helperText={formatErrors[column.id]}
                                    />
                                    <Tooltip title="Use yyyy, mm, dd, HH, MM, SS, A, Z and separators (-/.)">
                                      <IconButton size="small" className="format-help-icon">
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {column.numberFormat && (
                            <div className="format-control">
                              <div className="format-content">
                                <FormControl size="small" className="format-select">
                                  <Select
                                    value={column.numberFormat}
                                    onChange={(e) =>
                                      handleColumnNumberFormatChange(
                                        column.id,
                                        e.target.value as string
                                      )
                                    }
                                    label="Number Format"
                                  >
                                    {NUMBER_FORMAT_OPTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        <div className="format-option">
                                          <span>{option.label}</span>
                                          {option.value !== 'custom' && option.value !== 'none' && (
                                            <span className="format-example">
                                              {getFormatExample(option.value, 'number')}
                                            </span>
                                          )}
                                        </div>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {showCustomNumberFormat[column.id] && (
                                  <div className="custom-format-container">
                                    <Input
                                      type="text"
                                      placeholder={PLACEHOLDERS.CUSTOM_NUMBER_FORMAT}
                                      value={customNumberFormat[column.id] || column.numberFormat || ''}
                                      onChange={(e) => handleCustomNumberFormatChange(column.id, e.target.value)}
                                      className="custom-format-input"
                                      error={!!formatErrors[column.id]}
                                      helperText={formatErrors[column.id]}
                                    />
                                    <Tooltip title="Use 0, #, ,, . for formatting">
                                      <IconButton size="small" className="format-help-icon">
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="add-column-section">
                <FormControl fullWidth>
                  <Select
                    value=""
                    displayEmpty
                    renderValue={() => (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        className="add-column-btn"
                        fullWidth
                      >
                        Add Column
                      </Button>
                    )}
                    onChange={(e) => {
                      const selectedLabel = e.target.value as string;
                      const selectedOption = availableColumnOptions.find(opt => opt.label === selectedLabel);
                      if (selectedOption) {
                        const columnKey = selectedOption.value;
                        const columnMeta = availableColumns[columnKey];
                        if (columnMeta) {
                          addColumn(columnKey, columnMeta);
                        } else {
                          console.warn("Column metadata not found for key:", columnKey);
                        }
                      }
                    }}
                    disabled={loading || availableColumnOptions.length === 0}
                    className="column-select"
                  >
                    {availableColumnOptions.map((option) => (
                      <MenuItem key={option.value} value={option.label}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </CardContent>
          </Card>

          <div className="action-buttons">
            <Button
              onClick={handleReset}
              variant="outlined"
              className="reset-btn"
              disabled={loading}
            >
              Reset
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={enableEmail}
                  onChange={(e) => setEnableEmail(e.target.checked)}
                  color="primary"
                />
              }
              label="Get Report in Email"
              className="email-switch"
            />

            <Button
              onClick={handleCreateReport}
              variant="contained"
              color="primary"
              className="create-btn"
              disabled={loading || !selectedTemplate || !Array.isArray(selectedColumns) || selectedColumns.length === 0 || !!errorMessage}
            >
              {loading ? "Creating..." : "Create Report"}
            </Button>
          </div>
        </>
      )}
      
    </div>
  );
};

export default TemplateReports;